import React from 'react';
import { Link, Redirect, useParams } from 'react-router-dom';
import { Icon } from '@iconify/react';
import Layout from '../../Layout/Layout';
import { AppButtonLink } from '../../components/AppButton/AppButton';
import { BOT_GUIDES, getGuideById } from './botGuides';
import './Learn.css';

function renderInlineMarkdown(text) {
    const parts = text.split(/(\*\*[^*]+\*\*|\[[^\]]+\]\([^)]+\))/g);
    return parts.map((part, index) => {
        if (part.startsWith('**') && part.endsWith('**')) {
            return <strong key={index}>{part.slice(2, -2)}</strong>;
        }

        const linkMatch = part.match(/^\[([^\]]+)\]\(([^)]+)\)$/);
        if (linkMatch) {
            const [, label, href] = linkMatch;
            const isExternal = /^https?:\/\//i.test(href);
            if (isExternal) {
                return (
                    <a
                        key={index}
                        href={href}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="learn-guide-inline-link"
                    >
                        {label}
                    </a>
                );
            }
            return (
                <Link key={index} to={href} className="learn-guide-inline-link">
                    {label}
                </Link>
            );
        }

        return part;
    });
}

function LearnGuidePage() {
    const { guideId } = useParams();
    const guide = getGuideById(guideId);

    if (!guide) {
        return <Redirect to="/learn" />;
    }

    const relatedGuides = BOT_GUIDES.filter((item) => item.id !== guide.id);

    return (
        <Layout>
            <div className="learn-page">
                <div className="learn-guide-page">
                    <Link to="/learn" className="learn-guide-back">
                        ← Back to Learn
                    </Link>

                    <section className="learn-guide-hero">
                        <div className="learn-guide-hero-top">
                            <span className="learn-guide-hero-icon" aria-hidden="true">
                                <Icon icon={guide.icon} />
                            </span>
                            <div>
                                <h1 className="learn-guide-hero-title">{guide.title} Setup Guide</h1>
                                <div className="learn-guide-hero-meta">
                                    <span className="learn-guide-badge">{guide.difficulty}</span>
                                    <span className="learn-guide-badge">{guide.timeEstimate}</span>
                                </div>
                            </div>
                        </div>
                        <p className="learn-guide-hero-subtitle">{guide.subtitle}</p>
                        <div className="learn-guide-cta-row">
                            <AppButtonLink to={guide.setupPath} variant="primary" size="md">
                                {guide.setupLabel}
                            </AppButtonLink>
                            <AppButtonLink to="/pricing" variant="secondary" size="md">
                                View plan limits
                            </AppButtonLink>
                        </div>
                    </section>

                    <section className="learn-guide-section">
                        <h2 className="learn-guide-section-title">Overview</h2>
                        <p className="learn-guide-section-text">{renderInlineMarkdown(guide.overview)}</p>
                    </section>

                    <section className="learn-guide-section">
                        <h2 className="learn-guide-section-title">Before you start</h2>
                        <ul className="learn-guide-list">
                            {guide.prerequisites.map((item) => (
                                <li key={item}>{renderInlineMarkdown(item)}</li>
                            ))}
                        </ul>
                    </section>

                    <section className="learn-guide-section">
                        <h2 className="learn-guide-section-title">Step-by-step setup</h2>
                        <ol className="learn-guide-steps">
                            {guide.steps.map((step, index) => (
                                <li key={step.title} className="learn-guide-step">
                                    <div className="learn-guide-step-header">
                                        <span className="learn-guide-step-number">{index + 1}</span>
                                        <h3 className="learn-guide-step-title">{step.title}</h3>
                                    </div>
                                    <p className="learn-guide-step-content">
                                        {renderInlineMarkdown(step.content)}
                                    </p>
                                    {step.tips && step.tips.length > 0 && (
                                        <div className="learn-guide-tips">
                                            <p className="learn-guide-tips-title">Tips</p>
                                            <ul className="learn-guide-tips-list">
                                                {step.tips.map((tip) => (
                                                    <li key={tip}>{renderInlineMarkdown(tip)}</li>
                                                ))}
                                            </ul>
                                        </div>
                                    )}
                                </li>
                            ))}
                        </ol>
                    </section>

                    <section className="learn-guide-section">
                        <h2 className="learn-guide-section-title">Troubleshooting</h2>
                        <ul className="learn-guide-list">
                            {guide.troubleshooting.map((item) => (
                                <li key={item}>{renderInlineMarkdown(item)}</li>
                            ))}
                        </ul>
                    </section>

                    <section className="learn-guide-section">
                        <h2 className="learn-guide-section-title">Other bot guides</h2>
                        <div className="learn-related-grid">
                            {relatedGuides.map((related) => (
                                <Link key={related.id} to={`/learn/${related.id}`} className="learn-related-link">
                                    {related.title} guide →
                                </Link>
                            ))}
                        </div>
                    </section>
                </div>
            </div>
        </Layout>
    );
}

export default LearnGuidePage;